import { getServiceSettings, IEvaServiceCallOptions } from "@springtree/eva-sdk-core-service";
import { DataModelsOrderTypes, IEvaServiceDefinition, SvcCreateOrder } from "@springtree/eva-services-core";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";

import { shoppingCartAtom, shoppingCartRequestAtom } from "~/atoms/cart";
import { useAuthContext } from "~/contexts/auth";
import { useEvaService } from "~/hooks/use-eva-service";
import { useGuestCartStorage } from "~/hooks/use-guest-cart-storage";
import Logger from "~/services/logger/logger";
import { CacheKeys } from "~/shared/cache-keys";

/**
 * Extended service call options
 * @deprecated doNotSetStale is no longer needed with react-query
 */
type ExtendedServiceCallOptions = IEvaServiceCallOptions & {
  doNotSetStale?: boolean;
};

type CartOperationOptions = {
  onSuccess?: (data: any) => void | Promise<void>;
  onError?: (error: Error) => void | Promise<void>;
  skipCartInvalidation?: boolean; // Skip cart invalidation (useful for auth operations)
};

/**
 * Hook for cart operations that automatically update cart state
 * Replaces hooks.useShoppingCartAdd and hooks.useShoppingCartModify
 */
export function useEvaCartOperation<T extends IEvaServiceDefinition>(
  serviceDefinition: new () => T,
  options?: CartOperationOptions
) {
  const callService = useEvaService(serviceDefinition);
  const cartRequest = useAtomValue(shoppingCartRequestAtom);
  const queryClient = useQueryClient();

  const executeOperation = useCallback(
    async (
      payload: any,
      serviceOptions?: ExtendedServiceCallOptions,
      targetOrderId?: string
    ): Promise<T["response"]> => {
      try {
        // Remove doNotSetStale from options before passing to service
        const { doNotSetStale, ...cleanOptions } = serviceOptions || {};
        const response = await callService(payload, cleanOptions);

        // Invalidate and refetch cart query (unless explicitly skipped)
        if (!options?.skipCartInvalidation) {
          try {
            // Use the targetOrderId if provided (for new orders), otherwise use current cart OrderID
            const orderIdToRefetch = targetOrderId || cartRequest?.OrderID;

            // Invalidate queries first to mark them as stale
            queryClient.invalidateQueries({
              queryKey: ["shoppingCart"],
            });

            await queryClient.refetchQueries({
              queryKey: ["shoppingCart", orderIdToRefetch],
              type: "active",
            });
          } catch (e) {
            // QueryClient might not be available in all contexts (e.g., during login)
            Logger.instance.debug("useEvaCartOperation: Could not refetch cart queries", e);
          }
        }

        if (options?.onSuccess) {
          await options.onSuccess(response);
        }

        return response;
      } catch (error) {
        Logger.instance.error("useEvaCartOperation: Operation failed", error);

        if (options?.onError) {
          await options.onError(error as Error);
        }

        throw error;
      }
    },
    [callService, cartRequest?.OrderID, options, queryClient]
  );

  return executeOperation;
}

/**
 * Hook specifically for operations that add to cart
 * Includes orderType and createNewOrderWhen parameters
 * Supports both callOptions and hookCallOptions for backwards compatibility
 *
 * Creates a new order if none exists or if the current order is completed/paid
 * Updates shoppingCartRequestAtom with the new OrderID
 */
export function useEvaCartAdd<T extends IEvaServiceDefinition>(
  serviceDefinition: new () => T,
  options?: CartOperationOptions
) {
  const operation = useEvaCartOperation(serviceDefinition, options);
  const cartRequest = useAtomValue(shoppingCartRequestAtom);
  const shoppingCart = useAtomValue(shoppingCartAtom);
  const setShoppingCartRequest = useSetAtom(shoppingCartRequestAtom);
  const createOrder = useEvaService(SvcCreateOrder);
  const { currentUserToken } = useAuthContext();
  const cache = useGuestCartStorage();

  return useCallback(
    async (params: {
      orderType?: number;
      createNewOrderWhen?: "paid" | "completed" | string;
      payload: any;
      callOptions?: ExtendedServiceCallOptions;
      hookCallOptions?: ExtendedServiceCallOptions;
    }) => {
      try {
        // Support both callOptions and hookCallOptions for backwards compatibility
        const serviceOptions = params.callOptions || params.hookCallOptions;

        // Determine if we need to create a new order
        const currentOrderId = cartRequest?.OrderID;
        const isCompleted = shoppingCart?.ShoppingCart?.IsCompleted === true;
        const isPaid = shoppingCart?.ShoppingCart?.IsPaid === true;
        const isEmpty = !shoppingCart?.ShoppingCart?.Lines || shoppingCart.ShoppingCart.Lines.length === 0;

        const createNewOrder =
          !currentOrderId ||
          isEmpty ||
          (params.createNewOrderWhen === "completed" && isCompleted) ||
          (params.createNewOrderWhen === "paid" && isPaid);

        let targetOrderId: string;

        if (createNewOrder) {
          Logger.instance.debug("useEvaCartAdd: Creating new order", {
            orderType: params.orderType,
            reason: !currentOrderId ? "no order" : isEmpty ? "empty cart" : isPaid ? "paid" : "completed",
            currentUserToken,
          });

          const newOrder = await createOrder(
            {
              Type: params.orderType ?? DataModelsOrderTypes.Sales,
            },
            serviceOptions
          );

          if (!newOrder?.OrderID) {
            throw new Error("Failed to create new order: OrderID is missing");
          }
          targetOrderId = `${newOrder.OrderID}`;
          // For guest users, capture and save the EVA application token
          // The SDK automatically captures the EVA-App-Token header from CreateOrder response
          // and stores it in memory. We need to persist it to localStorage for cart recovery.
          if (!currentUserToken) {
            const settings = getServiceSettings();
            const applicationTokens = settings.applicationTokens || {};
            const evaOrigin = Object.keys(applicationTokens)[0];
            const appToken = evaOrigin ? applicationTokens[evaOrigin] : undefined;

            if (appToken && evaOrigin) {
              cache.set(CacheKeys.ANONYMOUS_APP_TOKEN, JSON.stringify({ token: appToken, origin: evaOrigin }));
            } else {
              Logger.instance.warn("useEvaCartAdd: No application token found after CreateOrder", {
                orderId: targetOrderId,
                hasOrigin: !!evaOrigin,
              });
            }
          }

          // Update the shopping cart request atom with the new order ID
          // CartSync will automatically save this OrderID to localStorage for guest users
          // allowing cart persistence across reloads and tabs
          setShoppingCartRequest({
            ...cartRequest,
            OrderID: targetOrderId,
          });

          // Logger.instance.debug(`useEvaCartAdd: Order created and OrderID set to ${targetOrderId}`, {
          //   willPersist: !currentUserToken, // Guest carts are persisted via localStorage
          // });
        } else {
          targetOrderId = `${currentOrderId}`;
        }

        // Merge OrderID into payload
        const enrichedPayload = {
          ...params.payload,
          OrderID: params.payload.OrderID || targetOrderId,
        };

        Logger.instance.debug(`useEvaCartAdd: adding to cart ${targetOrderId}`, {
          productID: enrichedPayload.ProductID,
        });

        // Pass targetOrderId to operation so it can refetch with the correct OrderID
        return await operation(enrichedPayload, serviceOptions, targetOrderId);
      } catch (error) {
        Logger.instance.error("useEvaCartAdd: failed to add to cart", error);
        throw error;
      }
    },
    [operation, cartRequest, shoppingCart, setShoppingCartRequest, createOrder, currentUserToken, cache]
  );
}

/**
 * Hook specifically for operations that modify cart
 * Supports both callOptions and hookCallOptions for backwards compatibility
 */
export function useEvaCartModify<T extends IEvaServiceDefinition>(
  serviceDefinition: new () => T,
  options?: CartOperationOptions
) {
  const operation = useEvaCartOperation(serviceDefinition, options);
  const cartRequest = useAtomValue(shoppingCartRequestAtom);

  return useCallback(
    async (params: {
      payload: any;
      callOptions?: ExtendedServiceCallOptions;
      hookCallOptions?: ExtendedServiceCallOptions;
    }) => {
      // Support both callOptions and hookCallOptions for backwards compatibility
      const serviceOptions = params.callOptions || params.hookCallOptions;

      // Merge OrderID from cart request into payload if not already present
      const enrichedPayload = {
        ...params.payload,
        OrderID: params.payload?.OrderID || cartRequest?.OrderID,
      };

      return operation(enrichedPayload, serviceOptions);
    },
    [operation, cartRequest?.OrderID]
  );
}
